import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { logoSrc } from "@/lib/nav";
import { Plus, UserCog, Upload } from "lucide-react";
import { Loading, EmptyState } from "@/components/common";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [staff, setStaff] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "", role: "staff" });
  const [supportWa, setSupportWa] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [bizName, setBizName] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [savingBiz, setSavingBiz] = useState(false);

  const load = useCallback(async () => setStaff((await api.get("/users", { params: { role: "staff" } })).data), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/settings").then((r) => { setSupportWa(r.data.support_whatsapp || ""); setSupportEmail(r.data.support_email || ""); }).catch(() => {});
    api.get("/business").then((r) => setBizName(r.data.name || "")).catch(() => {});
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name || !f.email || !f.password) { toast.error("Name, email and password required"); return; }
    try { await api.post("/users", f); toast.success("Staff member added"); setOpen(false); setF({ name: "", email: "", password: "", phone: "", role: "staff" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveSettings = async () => {
    try { await api.put("/settings", { support_whatsapp: supportWa.trim(), support_email: supportEmail.trim() }); toast.success("Support contact saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveBusiness = async () => {
    if (!bizName.trim()) { toast.error("Business name is required"); return; }
    setSavingBiz(true);
    try {
      await api.put("/business", { name: bizName.trim() });
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post("/business/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setLogoFile(null);
      }
      await refresh();
      toast.success("Business profile updated");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingBiz(false); }
  };

  if (staff === null) return <Loading />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-display text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage staff accounts and business details.</p>
      </div>

      <div className="bg-white border rounded-xl shadow-sm p-5">
        <h3 className="font-semibold font-display text-slate-900">Business Profile</h3>
        <p className="text-sm text-slate-500 mt-1">Your business name and logo appear across the dashboard and the customer portal.</p>
        <div className="mt-4 flex flex-col sm:flex-row items-start gap-5">
          <div className="flex flex-col items-center gap-2">
            <div className="h-20 w-20 rounded-xl border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden">
              {logoFile ? (
                <img src={URL.createObjectURL(logoFile)} alt="preview" className="h-full w-full object-cover" />
              ) : logoSrc(user) ? (
                <img src={logoSrc(user)} alt="logo" className="h-full w-full object-cover" />
              ) : (
                <span className="text-2xl font-bold font-display text-slate-400">{(bizName || "V").slice(0, 1).toUpperCase()}</span>
              )}
            </div>
            <label className="text-xs text-blue-700 font-medium cursor-pointer inline-flex items-center gap-1 hover:underline" data-testid="logo-upload-label">
              <Upload className="h-3.5 w-3.5" /> Change logo
              <input type="file" accept="image/*" className="hidden" data-testid="logo-input" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
            </label>
          </div>
          <div className="flex-1 w-full space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Business Name</Label>
                <Input data-testid="business-name-input" value={bizName} onChange={(e) => setBizName(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label>Signed in as</Label>
                <Input value={user?.email || ""} disabled className="mt-1.5 bg-slate-50" />
              </div>
            </div>
            <Button data-testid="save-business" onClick={saveBusiness} disabled={savingBiz} className="bg-[#1e3a8a] hover:bg-[#1e40af]">
              {savingBiz ? "Saving…" : "Save Business Profile"}
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-xl shadow-sm p-5">
        <h3 className="font-semibold font-display text-slate-900">Customer Support Contact</h3>
        <p className="text-sm text-slate-500 mt-1">This WhatsApp number is shown to customers in their portal for support.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Support WhatsApp Number</Label>
            <Input data-testid="support-whatsapp" value={supportWa} onChange={(e) => setSupportWa(e.target.value)} placeholder="e.g. 919876543210" className="mt-1.5" />
            <p className="text-xs text-slate-400 mt-1">Include country code. 10-digit numbers are assumed to be Indian (+91).</p>
          </div>
          <div>
            <Label>Support Email (optional)</Label>
            <Input data-testid="support-email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} placeholder="support@example.com" className="mt-1.5" />
          </div>
        </div>
        <Button data-testid="save-settings" onClick={saveSettings} className="mt-4 bg-[#1e3a8a] hover:bg-[#1e40af]">Save Support Contact</Button>
      </div>

      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b">
          <h3 className="font-semibold font-display text-slate-900">Staff Members</h3>
          <Button size="sm" data-testid="add-staff-button" onClick={() => setOpen(true)} className="bg-[#1e3a8a] hover:bg-[#1e40af]"><Plus className="h-4 w-4 mr-1.5" /> Add Staff</Button>
        </div>
        {staff.length === 0 ? <EmptyState icon={UserCog} title="No staff members" sub="Add staff to assign orders and tasks." /> : (
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Added</TableHead></TableRow></TableHeader>
            <TableBody>
              {staff.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium text-slate-900">{s.name}</TableCell>
                  <TableCell>{s.email}</TableCell>
                  <TableCell className="text-slate-600">{s.phone || "—"}</TableCell>
                  <TableCell className="text-slate-500">{formatDate(s.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Add Staff Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full Name *</Label><Input data-testid="sf-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Email *</Label><Input data-testid="sf-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Password *</Label><Input data-testid="sf-password" type="text" value={f.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input data-testid="sf-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-staff" onClick={save} className="bg-[#1e3a8a]">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
